<script setup lang="ts">
import { AggregateBlueprint } from '@/components/builder/base/blueprints/AggregateBlueprint';
import { DetailsBuilderContract } from '@/components/builder/details';
import { DocumentDetailsType } from '.';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useLocalization } from '@/plugins/localization';
import FormsService, { RelatedDocumentField } from '@/modules/studio/forms/services/FormsService';
import IdeoFormSelect from '@/components/ideo/form-select/IdeoFormSelect.vue';
import RelatedDocuments from '@/modules/low-code/components/RelatedDocuments.vue';
import ColumnsCustomizer from '@/components/common/dynamic-grid/ColumnsCustomizer.vue';
import FieldFilters from '@/components/forms/properties/FieldFilters.vue';
import properties from '../../properties';
import { RowContract } from '../row';
import { computedAsync } from '@vueuse/core';
import AutocompleteService from '@/modules/core/common/services/AutocompleteService';
import Pager from '@/helpers/Pager';
import { Drop, Drag } from '@/components/dragdrop';
import ComponentButton from '@/components/dynamic-details/helpers/ComponentButton.vue';
import DesignRuller from '../../helpers/DesignRuller.vue';
import DetailsComponentWrapper from '../../helpers/DetailsComponentWrapper.vue';
import DetailsComponent from '@/modules/low-code/views/Details.vue';
import ComponentSelection from '../../helpers/ComponentSelection.vue';
import { LogitoFieldType } from '@/components/forms/blueprints/logito-field/index';
import RowBlueprint from '../row/Blueprint.vue';
import lodashLast from 'lodash/last';
import FieldConfigsService, {
    FieldType,
} from '@/modules/low-code/services/FieldConfigsService';
import { getIconButton } from '@/components/common/dynamic-grid/helpers/TypeOfData';
import axios from 'axios';
import { Option } from '@/helpers/Interfaces';
import { SectionColors } from '../section';

defineOptions({
    name: 'DocumentDetailsBlueprint',
    components: {
        RelatedDocuments,
        ColumnsCustomizer,
        FieldFilters,
        Drop,
        Drag,
        DesignRuller,
        ComponentButton,
        DetailsComponentWrapper,
        DetailsComponent,
        ComponentSelection,
        RowBlueprint,
        ...properties,
    },
});

interface Props {
    details: DetailsBuilderContract;
    parent: AggregateBlueprint;
    blueprint: DocumentDetailsType;
}

const props = defineProps({
  "details": null,
  "parent": null,
  "blueprint": null
});

const route = useRoute();
const { $t, $i18n } = useLocalization();

const colorOptions: Option<SectionColors>[] = [
    {value: SectionColors.None, text: $t('[[[Brak]]]')},
    {value: SectionColors.Primary, text: $t('[[[Podstawowy]]]')},
    {value: SectionColors.Secondary, text: $t('[[[Drugorzędny]]]')},
    {value: SectionColors.Success, text: $t('[[[Sukces]]]')},
    {value: SectionColors.Info, text: $t('[[[Informacja]]]')},
    {value: SectionColors.Warning, text: $t('[[[Ostrzeżenie]]]')},
    {value: SectionColors.Danger, text: $t('[[[Zagrożenie]]]')},
    {value: SectionColors.Light, text: $t('[[[Jasny]]]')},
    {value: SectionColors.Dark, text: $t('[[[Ciemny]]]')},
];

const fieldOptions = ref<RelatedDocumentField[]>([]);
const showExtraContent = ref(false);
const documentData = ref<any>(null);

const isLoading = ref(false);
const error = ref<string | null>(null);
const documentFields = ref<any[]>([]);

const blueprint = computed(() => props.blueprint);
const design = computed(() => props.details.designMode());
const moduleId = computed(() => route.params.moduleId as string);
const publicId = computed(() => route.params.publicId as string);
const licence = computed(() => route.params.licence as string);

const details = computed(() => props.details);
const rows = computed<RowContract[]>(() =>
{
    return props.blueprint.components as RowContract[];
});

const last = computed<RowContract>(() => lodashLast(rows.value));
const lastComponentsLength = computed(() => last.value?.components?.length || 0);
const defaultFormLang = computed(() => $t(`[[[Pola z formularza: ]]] ${fieldOptions.value.find(p => p.key == blueprint.value.relatedDocumentField)?.value}`));
const translatedLocale = computed(() => $i18n.shortLocale());

const moduleControls = computedAsync(async () =>
{
    if (!design.value || moduleId.value == null || blueprint.value.relatedDocumentField == null || fieldOptions.value.length == 0) return [];

    const fields = await AutocompleteService.fetchOptionsByCustomEndpoint(
        `admin/forms/module-fields/${getCurrentFieldOptionModuleId(blueprint.value.relatedDocumentField)}`,
        null,
        new Pager(1, 999, '', 'ASC')
    );

    if (!fields) return [];

    const fieldsData = fields.items.map((item) => item.result).map((p) => ({ ...p, key: p.key, value: p.value }));

    await fetchDocumentFields(fieldsData);

    return fieldsData;
});

function addRow(before: RowContract = null)
{
    if (rows.value.length > 0 && last.value?.components?.length === 0 && !before)
    {
        return;
    }

    details.value.layout.addRow(blueprint.value, before);
}

function addComponent(before: RowContract = null)
{
    const newId = details.value.schema.newId();
    const row = before || last.value;

    if (!row) return;

    const classInstance = new LogitoFieldType(newId, 'defaultField');

    row.components.push(classInstance);
}

function getCurrentFieldOptionModuleId(key: string): number
{
    const field = fieldOptions.value.find(p => p.key == key);

    return field?.moduleId;
}

async function onFieldChanged(fieldName: any): Promise<void>
{
    const field = fieldOptions.value.find(p => p.key == fieldName);

    blueprint.value.moduleId = field?.moduleId;
}

function dropComponent(component: any, before: RowContract = null)
{
    if (!component)
    {
        return;
    }

    const row = details.value.layout.addRow(blueprint.value, before);

    const newId = details.value.schema.newId();

    const componentName = component.control;

    const classInstance = new LogitoFieldType(newId, componentName);

    row.components.push(classInstance);

    details.value.clipboard.cut(component);
    details.value.clipboard.paste(row);
}

async function fetchDocumentData()
{
    if (!design.value && blueprint.value.name && licence.value && publicId.value)
    {
        try
        {
            isLoading.value = true;
            error.value = null;

            const response = await axios.get(`${licence.value}/${publicId.value}/document-details/${blueprint.value.name}`);

            documentData.value = response.data;
        }
        catch (ex: any)
        {
            error.value = ex.message || 'Błąd podczas pobierania danych dokumentu';
            console.error('Błąd podczas pobierania danych dokumentu:', ex);
        }
        finally
        {
            isLoading.value = false;
        }
    }
}

async function fetchDocumentFields(fieldsData: any[] = null)
{
    try
    {
        let response: any;

        if (fieldsData)
        {
            response = fieldsData.reduce((acc, field) =>
            {
                acc[field.key] = field;

                return acc;
            }, {});
        }
        else
        {
            const modulePublicId = fieldOptions.value.find(p => p.key == blueprint.value.relatedDocumentField).modulePublicId;

            response = await FieldConfigsService.getFieldConfigDefaultSettings(modulePublicId, 'details');
        }

        documentFields.value = Object.entries(response).map(([key, value]) => ({
            blueprint: (value as any).blueprint ?? null,
            type: 'field',
            name: (value as any).headerName || (value as any).value,
            group: 'form-fields',
            nameDictionary: (value as any).headerNameDictionary || { pl: (value as any).value },
            form: (value as any).formName || defaultFormLang.value,
            control: key,
            icon: getIconButton((value as any).type || (value as any).fieldType),
            fieldType: (value as any).type || (value as any).fieldType,
        }));
    }
    catch (ex: any)
    {
        if (ex.code === 400) alert(ex.message);
    }
}

async function fetchFieldOptions(): Promise<void>
{
    fieldOptions.value = await FormsService.getRelatedDocumentFields(moduleId.value);
}

watch([route, () => blueprint.value.name], async () =>
{
    if (!design.value)
    {
        await fetchDocumentData();
    }
}, { deep: true });

watch(lastComponentsLength, (length: number): void =>
{
    if (length && length > 0)
    {
        props.details.layout.addRow(props.blueprint);
    }
});

if (props.blueprint.components.length == 0)
{
    props.details.layout.addRow(props.blueprint);
}

onMounted(async () =>
{
    if (design.value)
    {
        await fetchFieldOptions();
    }
    else
    {
        await fetchDocumentData();
    }
});
</script>

<template>
    <details-component-wrapper class="details-component" :details="details" :parent="parent" :blueprint="blueprint" :disabled="false">
        <template #default>
            <fieldset :class="{'design': design, [`alert alert-${blueprint.color}`]: blueprint.color != 'none'}">
                <legend class="title-document-details" v-if="blueprint.showTitle && blueprint.title">
                    {{ blueprint.title[translatedLocale] ?? $t('[[[Szczegóły dokumentu]]]') }}
                </legend>

                <p class="description" v-if="blueprint.description && blueprint.description[translatedLocale]">{{ blueprint.description[translatedLocale] }}</p>

                <template v-if="design">
                    <span v-if="blueprint.relatedDocumentField == null">
                        {{ $t('[[[Wybierz pole dokumentu]]]') }}
                    </span>
                    <div v-else class="rows">
                        <template v-for="(component, index) in rows" :key="component.id">
                            <dynamic-details-design-ruller
                                :title="$t('[[[Wstaw kontrolkę]]]')"
                                :details="details"
                                :before="component"
                                @insert="addRow"
                                @add="addComponent"
                                @drop="dropComponent"
                                v-if="component.components.length > 0"
                            />
                            <component
                                :is="details.schema.designer(`dynamic-details-${component.type}`)"
                                :details="details"
                                :parent="blueprint"
                                :blueprint="rows[index]"
                                :document-fields="documentFields"
                                :document-data="documentData"
                            ></component>
                        </template>
                    </div>
                </template>
                <template v-else>
                    <div v-if="isLoading" class="loading-spinner">
                        <span>{{ $t('[[[Ładowanie danych...]]]') }}</span>
                    </div>
                    <div v-else-if="error" class="error-message">
                        <span>{{ error }}</span>
                    </div>
                    <div v-else-if="documentData" class="document-data document-details-container">
                        <div class="rows">
                            <template v-for="(component, index) in rows" :key="component.id">
                                <component
                                    :is="details.schema.designer(`dynamic-details-${component.type}`)"
                                    :details="details"
                                    :parent="blueprint"
                                    :blueprint="rows[index]"
                                    :document-fields
                                    :document-data="documentData"
                                ></component>
                            </template>
                        </div>
                    </div>
                    <div v-else class="no-data">
                        <span>{{ $t('[[[Brak danych do wyświetlenia]]]') }}</span>
                    </div>
                </template>
            </fieldset>
        </template>
        <template #properties>
            <field-name v-model="blueprint.name" />
            <ideo-form-group v-if="!showExtraContent">
                <label class="related-document-field-label">{{ $t('[[[Pole z dokumentem]]]') }}</label>
                <ideo-form-select
                    v-model="blueprint.relatedDocumentField"
                    :options="fieldOptions"
                    :text-field="'value'"
                    :value-field="'key'"
                    @update:modelValue="onFieldChanged"
                    class="related-document-field"
                />
                <ideo-form-localize v-slot="{ locale }">
                    <field-text
                        v-model="blueprint.title[locale]"
                        :label="$t('[[[Tytuł]]]')"
                    />
                </ideo-form-localize>
                <field-checkbox v-model="blueprint.showTitle" :label="$t('[[[Wyświetlaj tytuł]]]')" />
                <ideo-form-localize v-slot="{ locale }">
                    <field-textarea
                        v-model="blueprint.description[locale]"
                        :label="$t('[[[Opis]]]')"
                    />
                </ideo-form-localize>
                <field-select v-model="blueprint.color" :options="colorOptions" :label="$t('[[[Akcent]]]')" />
                <field-visible :blueprint="blueprint" :label="$t('[[[Widoczność]]]')" />
            </ideo-form-group>
        </template>
    </details-component-wrapper>
</template>

<style scoped>
fieldset {
    border: 1px solid var(--bs-border-color);
    border-radius: 3px;
    padding: 8px;
}

.title-document-details {
    display: block;
    font-size: 1rem;
    font-weight: 400;
    color: var(--bs-card-color);
}

.details-component {
    display: flex;
    flex-direction: column;

    &.design {
        margin-top: 24px;
        border: 2px solid var(--bs-border-color);
    }

    &.selected {
        border-color: var(--bs-success);
    }
}

.add-wrapper {
    pointer-events: all;
    cursor: pointer;
    padding: 2px;

    .btn-placeholder {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        padding: 6px;
        background: transparent;
        border: 1px dashed var(--bs-border-color);
        border-radius: 3px;
        color: var(--bs-color);
        transition: all 0.25s ease-in-out;

        &:hover {
            background: var(--bs-secondary-bg);
        }
    }
}

.module-controls {
    display: flex;
    flex-wrap: wrap;
}

.loading-spinner, .error-message, .no-data {
    padding: 1rem;
    text-align: center;
}

.error-message {
    color: var(--bs-danger);
}

.document-data {
    width: 100%;
}

.related-document-field-label {
    margin-top: 0.5rem;
    display: block;
}

.related-document-field {
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
}
</style>
